import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { GraduationCap, ArrowLeft, CheckCircle2, XCircle, RotateCcw, ListChecks, ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";

const LETTERS = ["A", "B", "C", "D"];

function AcademyHeader() {
  return (
    <div data-testid="academy-header" className="mb-6">
      <Link to="/app/readiness" data-testid="back-to-academy" className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-[#0A2A5E]"><ArrowLeft className="w-4 h-4" /> LUMIS AI Academy</Link>
      <div className="mt-3 flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-[#0A2A5E] flex items-center justify-center"><GraduationCap className="w-5 h-5 text-[#F5A623]" /></div>
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#C7761A] font-bold">LUMIS AI Academy · Learning activity</div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#0A2A5E]">LUMIS AI™ Workforce Readiness Practice Check</h1>
        </div>
      </div>
    </div>
  );
}

function Intro({ meta, onStart }) {
  return (
    <Card data-testid="practice-intro" className="p-6 sm:p-8 bg-white border-slate-200 max-w-3xl">
      <p className="text-base text-slate-700 leading-relaxed">{meta.intro}</p>
      <p data-testid="practice-disclaimer" role="note" className="mt-4 p-3 rounded-lg bg-[#FFF7E8] border border-[#F5A623] text-sm text-[#7C3F00]">{meta.disclaimer}</p>
      <div className="mt-4 text-xs text-slate-500">{meta.total} questions · {meta.attempts > 0 ? `You have completed this ${meta.attempts} time(s) before.` : "First attempt"}</div>
      <Button data-testid="practice-start-btn" onClick={onStart} className="mt-6 bg-[#0A2A5E] hover:bg-[#0F3F8A] text-white h-11 px-6">Start Practice Check</Button>
    </Card>
  );
}

function Question({ q, index, total, onAnswered, onNext }) {
  const [choice, setChoice] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const feedbackRef = useRef(null);
  useEffect(() => { setChoice(null); setResult(null); }, [q.id]);
  useEffect(() => { if (result) feedbackRef.current?.focus(); }, [result]);

  const submit = async () => {
    if (choice == null) return;
    setBusy(true);
    try {
      const { data } = await api.post("/practice-check/check", { question: index, choice });
      setResult(data); onAnswered(index, choice, data);
    } catch { toast.error("Could not check the answer"); }
    finally { setBusy(false); }
  };
  const last = index === total - 1;

  return (
    <Card data-testid={`practice-question-${index + 1}`} className="p-6 sm:p-8 bg-white border-slate-200 max-w-3xl">
      <div className="flex items-center justify-between gap-3 text-xs text-slate-500">
        <span data-testid="practice-progress" aria-live="polite" className="font-semibold text-[#0A2A5E]">Question {index + 1} of {total}</span>
        <span className="uppercase tracking-widest font-bold text-[#C7761A]">{q.category}</span>
      </div>
      <Progress value={((index + (result ? 1 : 0)) / total) * 100} className="h-1.5 mt-2" aria-hidden />
      <fieldset className="mt-5">
        <legend id={`q-${q.id}`} className="font-display text-lg font-bold text-[#0A2A5E] leading-snug">{q.question}</legend>
        <div role="radiogroup" aria-labelledby={`q-${q.id}`} className="mt-4 space-y-2">
          {q.choices.map((c, i) => {
            const state = result ? (i === result.correct_answer ? "correct" : i === choice ? "wrong" : "") : (choice === i ? "selected" : "");
            const cls = { correct: "border-emerald-500 bg-emerald-50", wrong: "border-rose-400 bg-rose-50", selected: "border-[#0A2A5E] bg-[#F5F9FE]", "": "border-slate-200 hover:border-[#F5A623]" }[state];
            return (
              <label key={i} data-testid={`practice-choice-${i}`} className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors focus-within:ring-2 focus-within:ring-[#F5A623] ${cls} ${result ? "cursor-default" : ""}`}>
                <input type="radio" name={`practice-${q.id}`} value={i} checked={choice === i} disabled={!!result} onChange={() => setChoice(i)} className="mt-1 accent-[#0A2A5E]" aria-label={`${LETTERS[i]}. ${c}`} />
                <span className="text-sm text-slate-800"><span className="font-bold text-[#0A2A5E] mr-1.5">{LETTERS[i]}.</span>{c}</span>
              </label>
            );
          })}
        </div>
      </fieldset>

      {!result ? (
        <Button data-testid="practice-submit-btn" onClick={submit} disabled={choice == null || busy} className="mt-5 bg-[#0A2A5E] hover:bg-[#0F3F8A] text-white">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit answer"}</Button>
      ) : (
        <div ref={feedbackRef} tabIndex={-1} data-testid="practice-feedback" role="status" aria-live="assertive" className={`mt-5 p-4 rounded-xl border outline-none ${result.correct ? "bg-emerald-50 border-emerald-200" : "bg-rose-50 border-rose-200"}`}>
          <div className={`flex items-center gap-2 font-bold ${result.correct ? "text-emerald-800" : "text-rose-800"}`}>
            {result.correct ? <CheckCircle2 className="w-5 h-5" /> : <XCircle className="w-5 h-5" />}
            <span data-testid="practice-verdict">{result.correct ? "Correct" : `Incorrect — the best response is ${LETTERS[result.correct_answer]}`}</span>
          </div>
          <p data-testid="practice-explanation" className="text-sm text-slate-700 mt-2 leading-relaxed">{result.explanation}</p>
          <Button data-testid={last ? "practice-view-results-btn" : "practice-next-btn"} onClick={onNext} className="mt-4 bg-[#0A2A5E] hover:bg-[#0F3F8A] text-white">{last ? "View Results" : "Next Question"}</Button>
        </div>
      )}
    </Card>
  );
}

function Results({ attempt, meta, onReview, onRetake }) {
  const nav = useNavigate();
  return (
    <Card data-testid="practice-results" className="p-6 sm:p-8 bg-white border-slate-200 max-w-3xl">
      <div className="text-xs uppercase tracking-widest text-[#C7761A] font-bold">Practice check complete</div>
      <div data-testid="practice-score" className="font-display text-3xl sm:text-4xl font-extrabold text-[#0A2A5E] mt-1">Your score: {attempt.score} out of {attempt.total}</div>
      <div data-testid="practice-result-label" className="inline-block mt-3 px-3 py-1 rounded-full bg-[#FDECD0] text-[#7C3F00] font-semibold text-sm">{attempt.result.label}</div>
      <p data-testid="practice-result-message" className="text-base text-slate-700 mt-3 leading-relaxed">{attempt.result.message}</p>
      <p data-testid="practice-result-disclaimer" role="note" className="mt-4 p-3 rounded-lg bg-[#FFF7E8] border border-[#F5A623] text-xs text-[#7C3F00]">{attempt.result_disclaimer}</p>
      <div className="mt-6 grid sm:grid-cols-2 gap-2">
        <Button data-testid="practice-review-btn" variant="outline" onClick={onReview} className="border-[#0A2A5E]/40"><ListChecks className="w-4 h-4 mr-2" /> Review Answers</Button>
        <Button data-testid="practice-retake-btn" variant="outline" onClick={onRetake} className="border-[#F5A623] text-[#7C3F00] hover:bg-[#FDECD0]"><RotateCcw className="w-4 h-4 mr-2" /> Retake Practice Check</Button>
        <Button data-testid="practice-return-btn" onClick={() => nav("/app/readiness")} className="bg-[#0A2A5E] hover:bg-[#0F3F8A] text-white"><GraduationCap className="w-4 h-4 mr-2" /> Return to LUMIS AI Academy</Button>
        {meta.academy_url ? (
          <a href={meta.academy_url} target="_blank" rel="noreferrer" data-testid="practice-explore-link" className="inline-flex items-center justify-center h-10 rounded-md border border-slate-300 text-sm font-medium hover:bg-slate-50"><ExternalLink className="w-4 h-4 mr-2" /> Explore Complete Academy Learning</a>
        ) : (
          <Button data-testid="practice-explore-btn" variant="outline" onClick={() => toast.info(meta.academy_coming_soon)} className="border-slate-300"><ExternalLink className="w-4 h-4 mr-2" /> Explore Complete Academy Learning</Button>
        )}
      </div>
      <div className="text-xs text-slate-400 mt-4">Attempt #{attempt.attempt_number} · {new Date(attempt.completed_at).toLocaleString()}</div>
    </Card>
  );
}

function Review({ meta, attempt, onBack }) {
  return (
    <div data-testid="practice-review" className="max-w-3xl space-y-3">
      {meta.questions.map((q, i) => {
        const r = attempt.results[i];
        return (
          <Card key={q.id} data-testid={`review-question-${i + 1}`} className={`p-5 bg-white border ${r.correct ? "border-emerald-200" : "border-rose-200"}`}>
            <div className="text-[10px] uppercase tracking-widest text-[#C7761A] font-bold">Question {i + 1} · {q.category}</div>
            <div className="font-semibold text-[#0A2A5E] mt-1">{q.question}</div>
            <div className="text-sm mt-2"><span className="text-slate-500">Your answer:</span> <span className={r.correct ? "text-emerald-800 font-semibold" : "text-rose-800 font-semibold"}>{LETTERS[attempt.answers[i]]}. {q.choices[attempt.answers[i]]}</span></div>
            {!r.correct && <div className="text-sm mt-1"><span className="text-slate-500">Best response:</span> <span className="text-emerald-800 font-semibold">{LETTERS[r.correct_answer]}. {q.choices[r.correct_answer]}</span></div>}
            <p className="text-xs text-slate-600 mt-2 leading-relaxed">{r.explanation}</p>
          </Card>
        );
      })}
      <Button data-testid="practice-review-back-btn" variant="outline" onClick={onBack}><ArrowLeft className="w-4 h-4 mr-2" /> Back to results</Button>
    </div>
  );
}

export default function PracticeCheck() {
  const [meta, setMeta] = useState(null);
  const [stage, setStage] = useState("intro");
  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [attempt, setAttempt] = useState(null);

  const load = () => api.get("/practice-check").then((r) => setMeta(r.data)).catch(() => toast.error("Could not load the practice check"));
  useEffect(() => { load(); }, []);

  const start = () => { setAnswers([]); setIndex(0); setAttempt(null); setStage("question"); };
  const onAnswered = (i, choice) => setAnswers((a) => { const n = [...a]; n[i] = choice; return n; });
  const next = async () => {
    if (index < meta.total - 1) { setIndex(index + 1); return; }
    try {
      const { data } = await api.post("/practice-check/attempts", { answers });
      setAttempt(data); setStage("results"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not record the attempt"); }
  };

  if (!meta) return <div className="p-10 text-center text-slate-500">Loading…</div>;
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <AcademyHeader />
      {stage === "intro" && <Intro meta={meta} onStart={start} />}
      {stage === "question" && <Question key={index} q={meta.questions[index]} index={index} total={meta.total} onAnswered={onAnswered} onNext={next} />}
      {stage === "results" && attempt && <Results attempt={attempt} meta={meta} onReview={() => setStage("review")} onRetake={start} />}
      {stage === "review" && attempt && <Review meta={meta} attempt={attempt} onBack={() => setStage("results")} />}
    </div>
  );
}
